import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import * as Sentry from '@sentry/react';
import { Document_Type_Enum } from '@kheops/graphql';
import { useSetAtom } from 'jotai';
import { useGetUploadUrlLazyQuery } from '../queries/__generated__/getUploadUrl.generated';
import FileUtils, { FileParameters } from '../common/utils/file.utils';
import { commonSnackbarPropsAtom } from '../common/state/state';

export type FileTypes = Document_Type_Enum | 'COMPANY_PHOTOS' | 'PACKAGING_PHOTOS' | 'USER_INVOICE';

export const SUPPORTED_IMAGE_ACCEPT: Record<string, string[]> = {
  'image/jp2': ['.jp2'],
  'image/jpe': ['.jpe'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/jpg': ['.jpg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

export const SUPPORTED_IMAGE_TYPES = Object.keys(SUPPORTED_IMAGE_ACCEPT);

interface UseUploadFile {
  handleFileChange: (file: File, type: FileTypes, pathId: string) => Promise<FileParameters>;
  isFileUploadLoading: boolean;
}

export const MAXIMUM_FILE_SIZE_IN_BYTES = 50 * 1024 * 1024;

export function useUploadFile(): UseUploadFile {
  const { t } = useTranslation(['order', 'error']);
  const [getUploadUrlLazyQuery] = useGetUploadUrlLazyQuery({ fetchPolicy: 'no-cache' });
  const [isFileUploadLoading, setIsFileUploadLoading] = useState(false);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);

  const handleFileChange = async (file: File, type: FileTypes, pathId: string): Promise<FileParameters> => {
    if (file.size > MAXIMUM_FILE_SIZE_IN_BYTES) {
      setCommonSnackbarProps({
        label: t('error:file_too_large', {
          size: FileUtils.FormatFileSizeLimitLabel(MAXIMUM_FILE_SIZE_IN_BYTES, i18n.language),
        }),
        snackbarProps: {
          open: true,
        },
        alertProps: {
          severity: 'error',
        },
      });
      throw new Error('File size exceeds limited size of 50MB');
    }
    setIsFileUploadLoading(true);

    const fileName = FileUtils.NormalizeFileName(file.name);

    const { data: uploadUrlData } = await getUploadUrlLazyQuery({
      variables: {
        fileType: type,
        fileOriginalName: fileName,
        pathId,
      },
    });

    const uploadUrl = uploadUrlData?.getUploadUrl?.uploadUrl;

    if (!uploadUrl) {
      throw new Error('Expected upload url to be defined');
    }

    const fileParams = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
    }).then((res) => {
      setIsFileUploadLoading(false);

      if (res.status === 200) {
        return FileUtils.GetFileParametersFromUrl(uploadUrl.split('?')[0]);
      } if (res.status >= 400) {
        setCommonSnackbarProps({
          label: t('order:document_upload_failed'),
          snackbarProps: {
            open: true,
          },
          alertProps: {
            severity: 'error',
          },
        });
        res.text().then((message) => {
          Sentry.captureException(new Error(message));
        });
      }

      throw new Error('Failed to upload to S3');
    });

    return fileParams;
  };

  return {
    handleFileChange,
    isFileUploadLoading,
  };
}
